import {
  DeleteTwoTone,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  Input,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import IconCard from "../../static/icon/IconCard";

const UserPromotion = () => {
  const history = useHistory();
  const { accessToken, adminRole } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [userPromotions, setUserPromotions] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentUserPromotion, setCurrentUserPromotion] = useState(null);

  useEffect(() => {
    async function getUserPromotions({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/user-promotion', window.location);
      url.searchParams.append('type', 'landlord');
      if (searchValue.firstName) url.searchParams.append('firstName', searchValue.firstName);
      if (searchValue.lastName) url.searchParams.append('lastName', searchValue.lastName);
      if (searchValue.email) url.searchParams.append('email', searchValue.email);
      if (searchValue.tel) url.searchParams.append('tel', searchValue.tel);
      if (searchValue.code) url.searchParams.append('code', searchValue.code);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, userPromotions } = await res.json();
        setTotal(total);
        setUserPromotions(userPromotions);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getUserPromotions(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Surname",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile number",
      dataIndex: "tel",
      key: "tel",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "License Plate",
      dataIndex: "licensePlate",
      key: "licensePlate",
    },
    {
      title: "Cluster Name",
      dataIndex: "merchantNameEns",
      key: "merchantNameEns",
      render: (merchantNameEns, userPromotion) => userPromotion.internal ? (
        <Tooltip title={merchantNameEns.join(', ')}>
          {merchantNameEns.length} merchant(s)
        </Tooltip>
      ) : 'Public Used',
    },
    {
      title: "Site Name",
      dataIndex: "landlordNameEns",
      key: "landlordNameEns",
      render: (landlordNameEns, userPromotion) => userPromotion.internal ? (
        <Tooltip title={landlordNameEns.join(', ')}>
          {landlordNameEns.length} landlord(s)
        </Tooltip>
      ) : 'Public Used',
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, userPromotion) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="Delete" className="mx-1">
            <DeleteTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => setCurrentUserPromotion(userPromotion)}
              twoToneColor="red"
            />
          </Tooltip>
        </span>
      ),
    },
  ].filter(col => !col.hidden);

  const [downloading, setDownloading] = useState(false);
  async function downloadXlsx({ searchValue }) {
    setDownloading(true);

    const url = new URL('/api/user-promotion', window.location);
    url.searchParams.append('mode', 'xlsx');
    url.searchParams.append('type', 'landlord');
    if (searchValue.firstName) url.searchParams.append('firstName', searchValue.firstName);
    if (searchValue.lastName) url.searchParams.append('lastName', searchValue.lastName);
    if (searchValue.email) url.searchParams.append('email', searchValue.email);
    if (searchValue.tel) url.searchParams.append('tel', searchValue.tel);
    if (searchValue.code) url.searchParams.append('code', searchValue.code);
    if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
    if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const data = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `Private-Station-Member_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No matched member found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setDownloading(false);
  }

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconCard /> &nbsp;&nbsp;Member
            </span>
          }
        />

        <Button
          className="mt-2 mr-3 flex items-center px-12 border-0"
          loading={downloading}
          type="primary"
          shape="round"
          style={{ background: "#1BC5BD" }}
          onClick={() => downloadXlsx(search)}
        >
          Export
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Name"
                  name="firstName"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Name"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Surname"
                  name="lastName"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Surname"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Email"
                  name="email"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Email"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Mobile number"
                  name="tel"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Mobile number"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Code"
                  name="code"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Code"
                    allowClear
                  />
                </Form.Item>
              </Col>
              {['admin'].includes(adminRole) && (
                <Col className="gutter-row" span={6}>
                  <Form.Item
                    label="Cluster Name"
                    name="merchantIds"
                    colon={false}
                    initialValue={[]}
                  >
                    <MerchantDropdown
                      allowClear
                      allowSearch
                      mode="multiple"
                      setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={14}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(userPromotion) => userPromotion.id}
          loading={loading}
          columns={columns}
          dataSource={userPromotions}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentUserPromotion}
        name="Member"
        type="user-promotion"
        editRecord={currentUserPromotion}
        onCancel={() => setCurrentUserPromotion(null)}
        onOk={() => {
          setCurrentUserPromotion(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default UserPromotion;
